import { useState } from "react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Trash2, Plus, Download, Loader2, ChevronDown, ChevronRight, Settings2 } from "lucide-react";

const DEFAULT_TJM = 450;

const RESPONSABLES = [
  "Dev Frontend",
  "Dev Backend",
  "Fullstack",
  "DevOps",
  "Designer",
  "Chef de projet",
  "QA / Testeur",
];

const PHASES = ["MVP", "V1", "V2", "V3"];
const PRIORITIES = ["P0", "P1", "P2"];
const STATUSES = ["A faire", "En cours", "Termine", "Bloque"];

const priorityColors = {
  P0: "bg-rose-100 text-rose-700",
  P1: "bg-amber-100 text-amber-700",
  P2: "bg-gray-100 text-gray-500",
};

const statusColors = {
  "A faire": "bg-gray-100 text-gray-600",
  "En cours": "bg-blue-100 text-blue-700",
  Termine: "bg-emerald-100 text-emerald-700",
  Bloque: "bg-rose-100 text-rose-700",
};

function emptyRow() {
  return {
    id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
    phase: "MVP",
    composant: "",
    tache: "",
    responsable: "Fullstack",
    jours: 1,
    priorite: "P1",
    statut: "A faire",
  };
}

export default function PlanningTable({ initialRows, onExport }) {
  const [rows, setRows] = useState(initialRows || []);
  const [collapsedPhases, setCollapsedPhases] = useState({});
  const [tjm, setTjm] = useState(DEFAULT_TJM);
  const [showSettings, setShowSettings] = useState(false);
  const [devCount, setDevCount] = useState(3);

  const updateRow = (id, field, value) => {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: value } : r))
    );
  };

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);
  const removeRow = (id) => setRows((prev) => prev.filter((r) => r.id !== id));

  const togglePhase = (phase) => {
    setCollapsedPhases((prev) => ({ ...prev, [phase]: !prev[phase] }));
  };

  // Group by phase
  const grouped = {};
  for (const phase of PHASES) {
    grouped[phase] = rows.filter((r) => r.phase === phase);
  }

  // Totals
  const totalJours = rows.reduce((s, r) => s + (parseInt(r.jours) || 0), 0);
  const totalBudget = totalJours * tjm;
  const totalDone = rows.filter((r) => r.statut === "Termine").reduce((s, r) => s + (parseInt(r.jours) || 0), 0);
  const progressPct = totalJours > 0 ? Math.round((totalDone / totalJours) * 100) : 0;

  // By responsable
  const byResp = {};
  for (const r of rows) {
    if (!byResp[r.responsable]) byResp[r.responsable] = 0;
    byResp[r.responsable] += parseInt(r.jours) || 0;
  }

  // By phase
  const byPhase = {};
  for (const r of rows) {
    if (!byPhase[r.phase]) byPhase[r.phase] = 0;
    byPhase[r.phase] += parseInt(r.jours) || 0;
  }

  // Export CSV
  const exportCsv = () => {
    const header = `Phase;Composant;Tache;Responsable;Jours;Budget EUR (TJM ${tjm});Priorite;Statut\n`;
    const body = rows
      .map(
        (r) =>
          `${r.phase};${r.composant};${r.tache};${r.responsable};${r.jours};${(parseInt(r.jours) || 0) * tjm};${r.priorite};${r.statut}`
      )
      .join("\n");
    const total = `\nTOTAL;;;${totalJours};${totalBudget};;`;
    const blob = new Blob([header + body + total], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.download = "planning.csv";
    a.href = url;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Settings bar */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setShowSettings((v) => !v)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${showSettings ? "bg-indigo-50 border-indigo-200 text-indigo-700" : "bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100"}`}
        >
          <Settings2 size={13} /> Paramètres
        </button>
        <span className="text-[10px] text-gray-400">TJM : <strong className="text-gray-700">{tjm} EUR</strong></span>
        <span className="text-[10px] text-gray-400">|</span>
        <span className="text-[10px] text-gray-400">Équipe : <strong className="text-gray-700">{devCount} dev{devCount > 1 ? "s" : ""}</strong></span>
        <span className="text-[10px] text-gray-400">|</span>
        <span className="text-[10px] text-gray-400">Avancement : <strong className="text-gray-700">{progressPct}%</strong></span>
      </div>

      {showSettings && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 grid grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] font-semibold text-gray-500 uppercase block mb-1">Taux journalier moyen (TJM)</label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={tjm}
                onChange={(e) => setTjm(Math.max(0, parseInt(e.target.value) || 0))}
                className="rounded-lg border-gray-200 text-sm w-28 font-mono"
                min={0}
                data-testid="tjm-input"
              />
              <span className="text-xs text-gray-500">EUR / jour</span>
            </div>
            <div className="flex gap-1.5 mt-2">
              {[350, 450, 550, 650, 800].map((v) => (
                <button
                  key={v}
                  onClick={() => setTjm(v)}
                  className={`px-2 py-1 rounded-md text-[10px] font-medium border transition-colors ${tjm === v ? "bg-indigo-100 border-indigo-300 text-indigo-700" : "bg-white border-gray-200 text-gray-500 hover:bg-gray-50"}`}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] font-semibold text-gray-500 uppercase block mb-1">Taille de l'équipe</label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={devCount}
                onChange={(e) => setDevCount(Math.max(1, parseInt(e.target.value) || 1))}
                className="rounded-lg border-gray-200 text-sm w-20 font-mono"
                min={1}
                data-testid="team-size-input"
              />
              <span className="text-xs text-gray-500">developpeur{devCount > 1 ? "s" : ""}</span>
            </div>
          </div>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-2">
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-center">
          <span className="text-xl font-bold font-mono text-indigo-700">{totalJours}</span>
          <span className="text-[10px] text-indigo-500 block">jours/homme</span>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
          <span className="text-xl font-bold font-mono text-emerald-700">{new Intl.NumberFormat("fr-FR").format(totalBudget)}</span>
          <span className="text-[10px] text-emerald-500 block">EUR ({tjm}/j)</span>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
          <span className="text-xl font-bold font-mono text-amber-700">{Math.ceil(totalJours / (5 * devCount))}</span>
          <span className="text-[10px] text-amber-500 block">semaines ({devCount} dev{devCount > 1 ? "s" : ""})</span>
        </div>
        <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 text-center">
          <span className="text-xl font-bold font-mono text-violet-700">{progressPct}%</span>
          <span className="text-[10px] text-violet-500 block">avancement</span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-indigo-500 to-violet-500 rounded-full transition-all duration-500"
          style={{ width: `${progressPct}%` }}
        />
      </div>

      {/* Mini Gantt */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
        <span className="text-[10px] font-semibold text-gray-400 uppercase block mb-2">Timeline estimee</span>
        <div className="flex flex-col gap-1">
          {PHASES.map((phase) => {
            const pj = byPhase[phase] || 0;
            if (pj === 0) return null;
            const weeks = Math.ceil(pj / (5 * devCount));
            const maxWeeks = Math.ceil(totalJours / (5 * devCount));
            const widthPct = maxWeeks > 0 ? Math.max(10, (weeks / maxWeeks) * 100) : 0;
            const phaseColors = { MVP: "bg-indigo-500", V1: "bg-blue-500", V2: "bg-amber-500", V3: "bg-emerald-500" };
            const phaseDone = rows.filter((r) => r.phase === phase && r.statut === "Termine").reduce((s, r) => s + (parseInt(r.jours) || 0), 0);
            const phasePct = pj > 0 ? Math.round((phaseDone / pj) * 100) : 0;
            return (
              <div key={phase} className="flex items-center gap-2">
                <span className="text-[10px] font-semibold text-gray-600 w-8">{phase}</span>
                <div className="flex-1 h-5 bg-gray-200 rounded-full overflow-hidden relative">
                  <div className={`h-full ${phaseColors[phase]} rounded-full transition-all duration-300 opacity-80`} style={{ width: `${widthPct}%` }} />
                  {phasePct > 0 && (
                    <div className={`absolute top-0 left-0 h-full ${phaseColors[phase]} rounded-full`} style={{ width: `${widthPct * phasePct / 100}%` }} />
                  )}
                  <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-white drop-shadow-sm">
                    {weeks} sem · {pj}j · {new Intl.NumberFormat("fr-FR").format(pj * tjm)} EUR
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Répartition */}
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-2.5">
          <span className="text-[10px] font-semibold text-gray-400 uppercase">Par profil</span>
          {Object.entries(byResp).sort((a, b) => b[1] - a[1]).map(([resp, j]) => (
            <div key={resp} className="flex justify-between text-xs mt-1">
              <span className="text-gray-600">{resp}</span>
              <span className="font-semibold text-gray-800">{j}j ({new Intl.NumberFormat("fr-FR").format(j * tjm)} EUR)</span>
            </div>
          ))}
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-2.5">
          <span className="text-[10px] font-semibold text-gray-400 uppercase">Par phase</span>
          {Object.entries(byPhase).map(([phase, j]) => (
            <div key={phase} className="flex justify-between text-xs mt-1">
              <span className="text-gray-600">{phase}</span>
              <span className="font-semibold text-gray-800">{j}j ({new Intl.NumberFormat("fr-FR").format(j * tjm)} EUR)</span>
            </div>
          ))}
        </div>
      </div>

      {/* Table grouped by phase */}
      {PHASES.map((phase) => {
        const phaseRows = grouped[phase];
        if (!phaseRows || phaseRows.length === 0) return null;
        const collapsed = collapsedPhases[phase];
        const phaseTotal = phaseRows.reduce((s, r) => s + (parseInt(r.jours) || 0), 0);

        return (
          <div key={phase} className="border border-gray-200 rounded-xl overflow-hidden">
            <button
              onClick={() => togglePhase(phase)}
              className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center gap-2">
                {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                <span className="font-semibold text-sm text-gray-900">{phase}</span>
                <span className="text-[10px] bg-gray-200 px-1.5 py-0.5 rounded-full text-gray-600">{phaseRows.length} taches</span>
              </div>
              <span className="text-xs font-mono text-gray-500">{phaseTotal}j / {new Intl.NumberFormat("fr-FR").format(phaseTotal * tjm)} EUR</span>
            </button>

            {!collapsed && (
              <div className="divide-y divide-gray-100">
                {phaseRows.map((row) => (
                  <div key={row.id} className="px-3 py-2 flex flex-col gap-1.5 hover:bg-gray-50/50">
                    <div className="flex items-start gap-2">
                      <Input
                        value={row.composant}
                        onChange={(e) => updateRow(row.id, "composant", e.target.value)}
                        placeholder="Composant / Vue / Onglet"
                        className="rounded-md border-gray-200 text-xs h-7 flex-1 font-semibold"
                      />
                      <select
                        value={row.priorite}
                        onChange={(e) => updateRow(row.id, "priorite", e.target.value)}
                        className={`text-[10px] font-semibold px-2 py-1 rounded-full border-0 cursor-pointer ${priorityColors[row.priorite]}`}
                      >
                        {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                      <select
                        value={row.statut}
                        onChange={(e) => updateRow(row.id, "statut", e.target.value)}
                        className={`text-[10px] font-semibold px-2 py-1 rounded-full border-0 cursor-pointer ${statusColors[row.statut]}`}
                      >
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <button onClick={() => removeRow(row.id)} className="text-gray-300 hover:text-rose-500 shrink-0 mt-0.5"><Trash2 size={13} /></button>
                    </div>
                    <Input
                      value={row.tache}
                      onChange={(e) => updateRow(row.id, "tache", e.target.value)}
                      placeholder="Description de la tache..."
                      className="rounded-md border-gray-200 text-[11px] h-7 text-gray-600"
                    />
                    <div className="flex items-center gap-2">
                      <select
                        value={row.responsable}
                        onChange={(e) => updateRow(row.id, "responsable", e.target.value)}
                        className="text-[11px] border border-gray-200 rounded-md px-2 py-1 bg-white flex-1"
                      >
                        {RESPONSABLES.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          value={row.jours}
                          onChange={(e) => updateRow(row.id, "jours", e.target.value)}
                          className="rounded-md border-gray-200 text-xs h-7 w-14 text-center font-mono"
                          min={0}
                        />
                        <span className="text-[10px] text-gray-400">j</span>
                      </div>
                      <span className="text-[10px] font-mono text-gray-400">{new Intl.NumberFormat("fr-FR").format((parseInt(row.jours) || 0) * tjm)} EUR</span>
                      <select
                        value={row.phase}
                        onChange={(e) => updateRow(row.id, "phase", e.target.value)}
                        className="text-[10px] border border-gray-200 rounded-md px-1.5 py-1 bg-white"
                      >
                        {PHASES.map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Actions */}
      <div className="flex gap-2">
        <button onClick={addRow} className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg text-xs font-semibold hover:bg-gray-200 transition-colors flex items-center justify-center gap-1.5">
          <Plus size={14} /> Ajouter une ligne
        </button>
        <button onClick={exportCsv} className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-xs font-semibold hover:bg-indigo-700 transition-colors flex items-center gap-1.5 shadow-sm">
          <Download size={14} /> Export CSV
        </button>
      </div>
    </div>
  );
}
